import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import ConfirmModal from "bundles/common/components/ConfirmModal";
import sh from "bundles/common/utils/sh";
import ConversationList from "./ConversationList";
import ConversationView from "./ConversationView";

const CONTEXT_OPEN_STORAGE_KEY = "sq_messages_context_open";

// Config-driven messaging container shared by recruiters and candidates.
// `config` carries the perspective: selfType, endpoints, the ActionCable consumer
// + actor channel, a peerOf() extractor, and optional sidebar/empty/header slots.
const Messaging = ({ config, onUnreadCountChange }) => {
  const {
    selfType,
    endpoints,
    consumer,
    actorChannel,
    actorId,
    peerOf,
    renderSidebar,
    emptyState,
    headerExtra,
    extraParams = {},
    scrollIntoViewOnLoad = false,
  } = config;

  const theme = useTheme();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const [pendingAction, setPendingAction] = useState(null);

  const [conversations, setConversations] = useState([]);
  const [view, setView] = useState("active");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(() => {
    const params = new URLSearchParams(location.search || "");
    const raw = params.get("conversation");
    return raw ? Number(raw) : null;
  });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contextOpen, setContextOpen] = useState(() => {
    try {
      return window.localStorage.getItem(CONTEXT_OPEN_STORAGE_KEY) !== "false";
    } catch {
      return true;
    }
  });

  const containerRef = useRef(null);
  const didScrollIntoViewRef = useRef(false);
  const selectedIdRef = useRef(selectedId);
  const viewRef = useRef(view);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const updateUnreadCount = useCallback(
    (list) => {
      if (typeof onUnreadCountChange === "function") {
        onUnreadCountChange(list.filter((c) => c.unread).length);
      }
    },
    [onUnreadCountChange]
  );

  const loadConversations = useCallback(
    (nextView = viewRef.current) => {
      return sh
        .get(endpoints.index, { params: { status: nextView } })
        .then((res) => {
          const list = res.data?.data || [];
          setConversations(list);
          if (nextView === "active") updateUnreadCount(list);
          return list;
        })
        .catch((err) => console.error(err));
    },
    [endpoints.index, updateUnreadCount]
  );

  const applyConversationUpdate = useCallback(
    (data) => {
      // New conversations and archived-view updates are simplest to resync wholesale.
      if (viewRef.current !== "active") return;

      setConversations((prev) => {
        if (!prev.some((c) => c.id === data.conversation_id)) {
          loadConversations();
          return prev;
        }

        const isOpen = selectedIdRef.current === data.conversation_id;
        const fromPeer = data.last_message?.sender_type !== selfType;

        const next = prev
          .map((c) =>
            c.id === data.conversation_id
              ? {
                ...c,
                last_message: data.last_message,
                last_message_at: data.last_message_at,
                unread: fromPeer && !isOpen ? true : isOpen ? false : c.unread,
              }
              : c
          )
          .sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));

        updateUnreadCount(next);
        return next;
      });
    },
    [loadConversations, updateUnreadCount, selfType]
  );

  const applyStatusChange = useCallback((data) => {
    // Only the blocker perceives the block; the blocked party sees no change.
    const mineBlocked = data.blocked && data.blocked_by_type === selfType;
    setConversations((prev) =>
      prev.map((c) =>
        c.id === data.conversation_id
          ? { ...c, status: data.status, blocked: mineBlocked, blocked_by_me: mineBlocked }
          : c
      )
    );
    setDetail((prev) =>
      prev && prev.id === data.conversation_id
        ? { ...prev, status: data.status, blocked: mineBlocked, blocked_by_me: mineBlocked }
        : prev
    );
  }, [selfType]);

  useEffect(() => {
    if (loading || !scrollIntoViewOnLoad || didScrollIntoViewRef.current) return;
    didScrollIntoViewRef.current = true;

    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      const top = containerRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    }, 250);

    return () => clearTimeout(timer);
  }, [loading, scrollIntoViewOnLoad]);

  useEffect(() => {
    loadConversations("active").finally(() => setLoading(false));
  }, [loadConversations]);

  useEffect(() => {
    if (!actorId) return undefined;

    const subscription = consumer.subscriptions.create(
      { channel: actorChannel, id: actorId },
      {
        received(data) {
          if (data?.type === "conversation.updated") applyConversationUpdate(data);
          else if (data?.type === "conversation.status_changed") applyStatusChange(data);
        },
      }
    );

    return () => subscription.unsubscribe();
  }, [actorId, actorChannel, consumer, applyConversationUpdate, applyStatusChange]);

  useEffect(() => {
    if (!selectedId) return undefined;

    const subscription = consumer.subscriptions.create(
      { channel: "ConversationChannel", id: selectedId },
      {
        received(data) {
          if (data?.type === "conversation.status_changed") {
            applyStatusChange(data);
            return;
          }
          if (data?.type !== "message.created") return;

          const message = data.message;
          setDetail((prev) => {
            if (!prev || prev.id !== data.conversation_id) return prev;
            if (prev.messages.some((m) => m.id === message.id)) return prev;
            return { ...prev, messages: [...prev.messages, message] };
          });

          if (message.sender_type !== selfType) {
            sh.put(endpoints.read(data.conversation_id), { authenticity_token: authenticityToken }).catch(() => {});
          }
        },
      }
    );

    return () => subscription.unsubscribe();
  }, [selectedId, consumer, applyStatusChange, selfType, endpoints, authenticityToken]);

  useEffect(() => {
    if (loading || isMobile || selectedId || conversations.length === 0) return;

    const latestId = conversations[0].id;
    setSelectedId(latestId);
    updateConversationParam(latestId);
  }, [loading, isMobile, selectedId, conversations, view]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }

    setDetailLoading(true);
    sh.get(endpoints.show(selectedId))
      .then((res) => {
        setDetail(res.data?.data || null);
        setConversations((prev) => {
          const next = prev.map((c) => (c.id === selectedId ? { ...c, unread: false } : c));
          updateUnreadCount(next);
          return next;
        });
      })
      .catch((err) => console.error(err))
      .finally(() => setDetailLoading(false));
  }, [selectedId, updateUnreadCount, endpoints]);

  const updateConversationParam = (conversationId) => {
    const params = new URLSearchParams(window.location.search || "");
    Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));
    if (conversationId) {
      params.set("conversation", conversationId);
    } else {
      params.delete("conversation");
    }
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  };

  const handleSelect = (conversationId) => {
    setSelectedId(conversationId);
    updateConversationParam(conversationId);
  };

  // Exposed to headerExtra (e.g. the candidate "New message" flow) so a freshly
  // created conversation can be opened and the list resynced.
  const openConversation = (conversationId) => {
    if (view !== "active") setView("active");
    loadConversations("active");
    handleSelect(conversationId);
  };

  const handleBackToList = () => {
    setSelectedId(null);
    updateConversationParam(null);
  };

  const handleViewChange = (nextView) => {
    if (nextView === view) return;
    setView(nextView);
    setSelectedId(null);
    updateConversationParam(null);
    setLoading(true);
    loadConversations(nextView).finally(() => setLoading(false));
  };

  const setContextOpenPersisted = (open) => {
    setContextOpen(open);
    try {
      window.localStorage.setItem(CONTEXT_OPEN_STORAGE_KEY, String(open));
    } catch {
      // localStorage unavailable — keep in-memory state only
    }
  };

  const handleMessageSent = (message) => {
    setDetail((prev) => {
      if (!prev) return prev;
      if (prev.messages.some((m) => m.id === message.id)) return prev;
      return { ...prev, messages: [...prev.messages, message] };
    });
    setConversations((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? {
            ...c,
            last_message: {
              body: message.body,
              sender_type: message.sender_type,
              created_at: message.created_at,
            },
            last_message_at: message.created_at,
          }
          : c
      )
    );
  };

  const performArchiveToggle = () => {
    if (!selectedId) return;
    const toggledId = selectedId;
    sh.put(endpoints.archive_status(toggledId), { authenticity_token: authenticityToken })
      .then(() => loadConversations())
      .then((list) => {
        // The toggled conversation leaves the current tab; move to the next one (or none).
        const remaining = (list || []).filter((c) => c.id !== toggledId);
        const nextId = !isMobile && remaining.length ? remaining[0].id : null;
        setDetail(null);
        setSelectedId(nextId);
        updateConversationParam(nextId);
      })
      .catch((err) => console.error(err));
  };

  const performBlockToggle = () => {
    if (!selectedId) return;
    sh.put(endpoints.block_status(selectedId), { authenticity_token: authenticityToken })
      .then((res) => {
        const data = res.data?.data;
        if (!data) return;
        setDetail((prev) => (prev ? { ...prev, status: data.status, blocked: data.blocked_by_me, blocked_by_me: data.blocked_by_me } : prev));
        setConversations((prev) =>
          prev.map((c) => (c.id === selectedId ? { ...c, status: data.status, blocked: data.blocked_by_me, blocked_by_me: data.blocked_by_me } : c))
        );
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ id: "messaging.error_sending" }), { variant: "error" });
      });
  };

  const requestArchiveToggle = () => {
    setPendingAction({ type: "archive", descriptionId: detail?.archived ? "messaging.confirm_unarchive" : "messaging.confirm_archive" });
  };

  const requestBlockToggle = () => {
    setPendingAction({ type: "block", descriptionId: detail?.blocked ? "messaging.confirm_unblock" : "messaging.confirm_block" });
  };

  const handleConfirmAction = () => {
    if (pendingAction?.type === "archive") performArchiveToggle();
    else if (pendingAction?.type === "block") performBlockToggle();
    setPendingAction(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <SquarehubLoader size={72} centered />
      </Box>
    );
  }

  const renderHeaderExtra = () =>
    typeof headerExtra === "function"
      ? headerExtra({ openConversation, reloadConversations: () => loadConversations() })
      : headerExtra;

  // Show the onboarding empty state only when the active inbox is empty; the list
  // (with its Active/Archived tabs) stays visible so archived chats remain reachable.
  const showEmptyState = view === "active" && conversations.length === 0 && Boolean(emptyState);

  const sidebar = selectedId && renderSidebar
    ? renderSidebar(detail, { onClose: () => setContextOpenPersisted(false), inline: isMobile })
    : null;

  return (
    <Box ref={containerRef} sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 3 }}>
      {renderHeaderExtra()}
      <Box sx={{ display: "flex", gap: 2, alignItems: "stretch", height: "80vh", minHeight: "600px" }}>
        {(!isMobile || !selectedId) && (
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            onSelect={handleSelect}
            searchEndpoint={endpoints.search}
            peerOf={peerOf}
            selfType={selfType}
            view={view}
            onViewChange={handleViewChange}
          />
        )}

        {(!isMobile || selectedId) && selectedId && (
          <ConversationView
            conversationId={selectedId}
            detail={detail}
            loading={detailLoading}
            onMessageSent={handleMessageSent}
            onBack={isMobile ? handleBackToList : null}
            contextOpen={contextOpen}
            onToggleContext={() => setContextOpenPersisted(true)}
            contextPanel={isMobile ? sidebar : null}
            showContextToggle={Boolean(renderSidebar)}
            selfType={selfType}
            peerOf={peerOf}
            messageEndpoint={endpoints.message}
            onArchiveToggle={requestArchiveToggle}
            onBlockToggle={requestBlockToggle}
          />
        )}

        {!isMobile && !selectedId && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.common.white,
              border: "1px solid hsla(160, 10%, 90%, 0.6)",
              borderRadius: "16px",
            }}
          >
            {showEmptyState ? (
              emptyState
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                <FormattedMessage id="messaging.select_conversation" />
              </Typography>
            )}
          </Box>
        )}

        {!isMobile && selectedId && contextOpen && sidebar}
      </Box>

      <ConfirmModal
        isOpen={Boolean(pendingAction)}
        setIsOpen={(open) => !open && setPendingAction(null)}
        handleConfirm={handleConfirmAction}
        description={intl.formatMessage({ id: pendingAction?.descriptionId || "are_you_sure" })}
      />
    </Box>
  );
};

export default Messaging;
